import { quat, vec3 } from 'gl-matrix';
import { RenderObject } from '../object/RenderObject';
import { BoneCapsule, BoneCollisionBody, BoneHitResult } from './BoneCollisionBody';
import { BoneHitDetection } from './BoneHitDetection';
import { BoneImpulseSystem } from './BoneImpulse';
import { GltfAnimation } from '../animation';

export interface MeleeAttackParams {
  range: number; // max distance attacker → target center (world units)
  angle: number; // total cone angle in radians (e.g. Math.PI/2 = 90°)
  power: number; // impulse magnitude (same scale as projectile magnitude)
  hitDelay: number; // seconds from trigger until hit check fires
}

interface PendingAttack {
  attacker: RenderObject;
  params: MeleeAttackParams;
  remaining: number;
}

/**
 * Melee attack resolver. Schedules a hit check after a delay (matching the
 * peak of a swing animation), then applies bone impulses to all targets in
 * front of the attacker within range.
 *
 * Hit detection is intentionally coarse — range + facing cone, no per-bone
 * collision. The visual reaction comes from BoneImpulseSystem.
 */
export class MeleeAttackSystem {
  private pending: PendingAttack[] = [];

  constructor(
    private boneHitDetection: BoneHitDetection,
    private boneImpulseSystem: BoneImpulseSystem,
    /**
     * Fired for every bone the melee swing connects with. Same signature as
     * projectile bone hits — `PhysicsFeature` routes both paths through a
     * single `onBoneHit` slot so app-level impact SFX wires up once.
     * `hitPoint` is synthesised from the capsule's world center (melee
     * detection is range + cone, not ray-cast, so there's no precise
     * intersection point to report).
     */
    private onHit: (hit: BoneHitResult) => void = () => {},
  ) {}

  /**
   * Schedule a melee attack from `attacker`. The actual hit check fires
   * after `params.hitDelay` seconds.
   */
  triggerAttack(attacker: RenderObject, params: MeleeAttackParams): void {
    this.pending.push({ attacker, params, remaining: params.hitDelay });
  }

  tick(dt: number): void {
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const p = this.pending[i];
      p.remaining -= dt;
      if (p.remaining <= 0) {
        this.resolveHit(p.attacker, p.params);
        this.pending.splice(i, 1);
      }
    }
  }

  private resolveHit(attacker: RenderObject, params: MeleeAttackParams): void {
    const attackerPos = attacker.trs.position as vec3;
    const forward = this.computeForward(attacker);
    const cosThreshold = Math.cos(params.angle / 2);

    for (const target of this.boneHitDetection.targets) {
      if (target.renderObject === attacker) continue;

      const targetPos = target.renderObject.trs.position as vec3;
      const toTarget = vec3.fromValues(
        targetPos[0] - attackerPos[0],
        0,
        targetPos[2] - attackerPos[2],
      );
      const dist = vec3.length(toTarget);
      if (dist > params.range || dist < 0.001) continue;

      vec3.scale(toTarget, toTarget, 1 / dist);
      if (vec3.dot(forward, toTarget) < cosThreshold) continue;

      const jointIndex = this.pickHitBone(target);
      if (jointIndex < 0) continue;

      this.boneImpulseSystem.applyImpulse(target.renderObject, jointIndex, forward, params.power);

      // Route the hit through the shared bone-hit slot so impact SFX (and
      // any other app-level reactions) fire for melee too. We synthesise a
      // BoneHitResult — there's no ray to derive a precise hitPoint from,
      // so fall back to the capsule's world center.
      const capsule = target.boneCapsules.find((c) => c.jointIndex === jointIndex);
      if (capsule) {
        const hitPoint = this.computeCapsuleCenter(target, capsule);
        // 3D Euclidean attacker→hitPoint so `BoneHitResult.distance` carries
        // the same semantics as the projectile-path producer (ray parametric
        // distance). The cone check above already used the XZ horizontal
        // distance, so this is just a final report-out conversion.
        this.onHit({
          target,
          boneCapsule: capsule,
          hitPoint,
          distance: vec3.distance(attackerPos, hitPoint),
        });
      }
    }
  }

  /**
   * World-space center of the bone capsule for the current animation frame.
   * For a sphere bone, that's the joint position; for a capsule bone, the
   * midpoint between the joint and its parent.
   */
  private computeCapsuleCenter(target: BoneCollisionBody, capsule: BoneCapsule): vec3 {
    const obj = target.renderObject;
    if (!obj.animationState) return vec3.clone(obj.trs.position as vec3);
    const frameSet = obj.animationState.getFrames();
    const { a, b } = target.getWorldCapsuleEndpoints(
      capsule,
      frameSet,
      obj.modelMatrix,
      obj.boneOverrides,
    );
    return vec3.fromValues((a[0] + b[0]) * 0.5, (a[1] + b[1]) * 0.5, (a[2] + b[2]) * 0.5);
  }

  /** Forward vector — strips the asset's rest orientation (`fixedRotationQuat`)
   *  from the current rotation so movement-driven yaw is what's left. */
  private computeForward(obj: RenderObject): vec3 {
    const yawOnly = quat.create();
    const restInverse = quat.invert(quat.create(), obj.fixedRotationQuat);
    quat.multiply(yawOnly, obj.trs.getRotationQuat(), restInverse);
    const forward = vec3.transformQuat(vec3.create(), [0, 0, 1], yawOnly);
    forward[1] = 0;
    return forward;
  }

  /**
   * Pick the bone closest to ~70% up the model's vertical extent — i.e.,
   * the chest area for a humanoid rig. Works in local space using bind
   * positions, so independent of world transform / rig naming.
   */
  private pickHitBone(target: BoneCollisionBody): number {
    const anim = target.renderObject.animation as GltfAnimation | undefined;
    if (!anim) return -1;

    const bindPositions = anim.computeBindPosePositions();
    const bounds = target.renderObject.bounds;
    const minY = bounds.localMin[1];
    const maxY = bounds.localMax[1];
    const targetY = minY + (maxY - minY) * 0.7;

    // Centerline X (assume model is roughly symmetric around X=0 in local space)
    const centerX = (bounds.localMin[0] + bounds.localMax[0]) * 0.5;

    let bestIdx = -1;
    let bestScore = Infinity;
    for (const capsule of target.boneCapsules) {
      const local = bindPositions[capsule.jointIndex];
      const dy = local[1] - targetY;
      const dx = local[0] - centerX;
      // Y distance dominates; lateral offset penalized to prefer the spine
      // over arm bones at the same height.
      const score = dy * dy + dx * dx * 2;
      if (score < bestScore) {
        bestScore = score;
        bestIdx = capsule.jointIndex;
      }
    }
    return bestIdx;
  }
}
